import {
    DetalizationType,
    TrafficSourceType,
    UsersSortType,
} from '@/features/analytics/constants'
import { InviteLinkWithAnalyticsType } from '@/shared/api'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import { LegendType } from 'recharts'
import { useInviteLinksStore } from '../../model'
import { generateUniqueColor } from '../../utils'
import { useChannelLinksByGroup } from '@/true-fsd/data/link'
import { useGroupNameChannels } from '@/widgets/analytics/invite-links/hooks/useGroupName'

interface RetentionDataPoint {
    index: number
    [key: string]: number
}
export const useRetentionChartData = ({ channelId }: { channelId: number }) => {
    const { groupId } = useParams<{ groupId: string }>()
    const numericGroupId = Number(groupId)

    const groupName = useGroupNameChannels({
        channelId,
    })

    const { data: linksByGroup = [] } = useChannelLinksByGroup({
        channel_id: channelId,
        channel_links_group_id: numericGroupId,
        filter: groupName !== 'Все ссылки',
    })

    const {
        selectedLinksRetentionByGroup,
        trafficSourceSortRetention,
        detalizationSortRetention,
        usersSortRetention,
        startDateRetention,
        endDateRetention,
    } = useInviteLinksStore()

    const selectedLinksIds = selectedLinksRetentionByGroup[numericGroupId] || []

    const selectedLinks = linksByGroup.filter((link) =>
        selectedLinksIds.includes(link.invite_link_id)
    )

    const retentionData = useMemo(() => {
        if (selectedLinks.length === 0) return []

        const filteredLinks = filterLinks(
            selectedLinks,
            trafficSourceSortRetention.value
        )

        const sortedData = sortData(
            filteredLinks,
            detalizationSortRetention.value,
            startDateRetention,
            endDateRetention
        )

        return calculateRetentionData(
            sortedData,
            usersSortRetention.value,
            detalizationSortRetention.value
        )
    }, [
        selectedLinks,
        trafficSourceSortRetention.value,
        detalizationSortRetention.value,
        startDateRetention,
        endDateRetention,
        usersSortRetention.value,
    ])

    const inviteLinks = useMemo(() => {
        return selectedLinks.map((link) => ({
            id: link.invite_link_id,
            name: link.invite_link_name,
            value: link.invite_link_name,
            type: 'line' as LegendType,
            color: generateUniqueColor(link.invite_link_id),
        }))
    }, [selectedLinks])

    const lastIndices = useMemo(() => {
        const indices: Record<string, number> = {}
        inviteLinks.forEach((link) => {
            const dataKey = link.id
            let lastIndex = -1
            retentionData.forEach((entry, idx) => {
                if (entry[dataKey] != null) {
                    lastIndex = idx
                }
            })
            indices[dataKey] = lastIndex
        })
        return indices
    }, [inviteLinks, retentionData])

    return { retentionData, inviteLinks, lastIndices }
}

const filterLinks = (
    links: InviteLinkWithAnalyticsType[],
    trafficSource: TrafficSourceType
): InviteLinkWithAnalyticsType[] => {
    return links.filter((link) => {
        const isInviteLink = link.invite_link_id !== undefined

        if (trafficSource !== 'allTraffic') {
            if (
                (trafficSource === 'inviteLinks' && !isInviteLink) ||
                (trafficSource === 'anySource' && isInviteLink)
            ) {
                return false
            }
        }

        return true
    })
}

const sortData = (
    links: InviteLinkWithAnalyticsType[],
    detalization: DetalizationType,
    startDate: string,
    endDate: string
): Map<string, InviteLinkWithAnalyticsType[]> => {
    const start = new Date(startDate)
    const end = new Date(endDate)

    const filteredLinks = links.filter((link) => {
        const linkDate = new Date(link.created_at)
        return linkDate >= start && linkDate <= end
    })

    const groupedData = new Map<string, InviteLinkWithAnalyticsType[]>()

    filteredLinks.forEach((link) => {
        const date = new Date(link.created_at)
        let key: string

        const weekStart = new Date(date.setDate(date.getDate() - date.getDay()))

        switch (detalization) {
            case 'daily':
                key = date.toISOString().split('T')[0]
                break
            case 'weekly':
                key = weekStart.toISOString().split('T')[0]
                break
            case 'monthly':
                key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                break
            default:
                key = date.toISOString().split('T')[0]
        }

        if (!groupedData.has(key)) {
            groupedData.set(key, [])
        }
        groupedData.get(key)!.push(link)
    })

    return new Map([...groupedData.entries()].sort())
}

const calculateRetentionData = (
    sortedData: Map<string, InviteLinkWithAnalyticsType[]>,
    usersSortRetention: UsersSortType,
    detalization: DetalizationType
): RetentionDataPoint[] => {
    const retentionData: RetentionDataPoint[] = [{ index: 0 }]
    for (const [_, links] of sortedData) {
        links.forEach((link) => {
            let currentSubscribers =
                usersSortRetention === 'allUsers'
                    ? link.total_subscribers
                    : link.total_premium_subscribers
            retentionData[0][link.invite_link_id] = 100

            if (!link.analytics) {
                console.log('No analytics data for link', link)
                return
            }

            link.analytics.forEach((analytics, index) => {
                if (
                    (detalization === 'weekly' && index % 7 === 0) ||
                    (detalization === 'monthly' && index % 30 === 0) ||
                    detalization === 'daily'
                ) {
                    const dataPointIndex = Math.floor(
                        index /
                            (detalization === 'weekly'
                                ? 7
                                : detalization === 'monthly'
                                  ? 30
                                  : 1)
                    )
                    const dataPoint = retentionData[dataPointIndex] || {
                        index: dataPointIndex,
                    }

                    let retentionRate: number

                    if (usersSortRetention === 'premiumUsers') {
                        retentionRate =
                            (currentSubscribers /
                                link.total_premium_subscribers) *
                            100
                    } else {
                        retentionRate =
                            (currentSubscribers / link.total_subscribers) * 100
                    }

                    dataPoint[link.invite_link_id] = retentionRate
                    if (!retentionData[dataPointIndex]) {
                        retentionData.push(dataPoint)
                    }
                }
                if (usersSortRetention === 'allUsers') {
                    currentSubscribers -= analytics.unsubscribed_count
                } else if (usersSortRetention === 'premiumUsers') {
                    currentSubscribers -= analytics.premium_unsubscribed_count
                }
            })
        })
    }

    return retentionData
}
